import React from 'react';

interface TriangleChartProps {
  data: {
    name: string;
    value: number;
    color?: string;
  }[];
  title?: string;
  className?: string;
  centerValue?: string;
}

const TriangleChart: React.FC<TriangleChartProps> = ({
  data,
  title,
  className,
  centerValue
}) => {
  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'];
  
  return (
    <div className={`bg-gray-900 rounded-lg p-4 ${className}`}>
      {title && (
        <div className="text-gray-900 text-sm font-medium mb-3">{title}</div>
      )}
      
      <div className="relative w-full h-48 flex items-center justify-center">
        <svg viewBox="0 0 200 200" className="w-full h-full max-w-48">
          {/* Triangle base */}
          <path
            d="M100 20 L170 160 L30 160 Z"
            fill="none"
            stroke="#374151"
            strokeWidth="2"
          />
          
          {/* Triangle sections */}
          {data.map((item, index) => {
            const totalValue = data.reduce((sum, d) => sum + d.value, 0);
            const percentage = (item.value / totalValue) * 100;
            const height = (percentage / 100) * 140; // 140 is the triangle height
            
            // Calculate triangle section path
            const baseY = 160 - (height * index / data.length);
            const topY = baseY - height / data.length;
            const baseWidth = (160 - baseY) * (140 / 140); // Proportional width
            const topWidth = (160 - topY) * (140 / 140);
            
            const leftBase = 100 - baseWidth / 2;
            const rightBase = 100 + baseWidth / 2;
            const leftTop = 100 - topWidth / 2;
            const rightTop = 100 + topWidth / 2;
            
            return (
              <g key={index}>
                <path
                  d={`M${leftTop} ${topY} L${rightTop} ${topY} L${rightBase} ${baseY} L${leftBase} ${baseY} Z`}
                  fill={item.color || colors[index % colors.length]}
                  opacity="0.8"
                />
                <text
                  x="100"
                  y={topY + (baseY - topY) / 2}
                  textAnchor="middle"
                  fill="white"
                  fontSize="10"
                  fontWeight="bold"
                >
                  {percentage.toFixed(0)}%
                </text>
              </g>
            );
          })}
          
          {/* Center value */}
          {centerValue && (
            <text
              x="100"
              y="105"
              textAnchor="middle"
              fill="white"
              fontSize="16"
              fontWeight="bold"
            >
              {centerValue}
            </text>
          )}
        </svg>
      </div>
      
      {/* Legend */}
      <div className="grid grid-cols-2 gap-2 mt-4">
        {data.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: item.color || colors[index % colors.length] }}
            />
            <span className="text-xs text-gray-600">{item.name}</span>
            <span className="text-xs text-gray-900 font-semibold">{item.value}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TriangleChart;